import { keyframes, styled } from '@/stitches/index.js';
import * as ModalPrimitive from '@radix-ui/react-dialog';

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
});

const fadeUp = keyframes({
  '0%': {
    opacity: 0,
    transform: 'translate(0, 1rem)',
  },
  '100%': {
    opacity: 1,
    transform: 'translate(0, 0)',
  },
});

const slideUp = keyframes({
  '0%': {
    transform: 'translate(0, 200%)',
  },
  '100%': {
    transform: 'translate(0, 0)',
  },
});

export const Overlay = styled(ModalPrimitive.Overlay, {
  '@media (prefers-reduced-motion: no-preference)': {
    animation: `${overlayShow} 250ms cubic-bezier(0.16, 1, 0.3, 1) forwards`,
  },
  backgroundColor: '$overlay20',
  inset: 0,
  position: 'fixed',
  zIndex: '$modalBackdrop',
});

export const Container = styled('div', {
  display: 'flex',
  overflow: 'scroll',
  variants: {
    variant: {
      drawer: {
        alignItems: 'flex-end',
        height: '100vh',
      },
      modal: {
        alignItems: 'flex-start',
        height: '100%',
        justifyContent: 'center',
        maxHeight: '100%',
        padding: '$48 $16',
      },
      modalSmol: {
        alignItems: 'flex-start',
        height: '100%',
        justifyContent: 'center',
        maxHeight: '100%',
        padding: '$48 $16',
      },
    },
  },
  zIndex: '$modalDialog',
});

export const Content = styled(ModalPrimitive.Content, {
  '&:focus': { outline: 'none' },
  backgroundColor: '$white',
  fontFamily: '$inter',
  variants: {
    variant: {
      drawer: {
        '@media (prefers-reduced-motion: no-preference)': {
          animation: `${slideUp} 250ms forwards`,
        },
        borderRadius: '$20 $20 0 0',
        bottom: 0,
        left: 0,
        padding: '$16 $18 $32',
        position: 'absolute',
        width: '100vw',
      },
      modal: {
        '@bp0': {
          padding: '$24 $16 $16 $16',
          width: '100%',
        },
        '@bp2': {
          maxWidth: '$800',
          padding: '$48',
          width: '$640',
        },
        '@media (prefers-reduced-motion: no-preference)': {
          animation: `${fadeUp} 250ms forwards`,
        },
        borderRadius: '$20',
        height: 'auto',
        margin: 'auto 0',
      },
      modalSmol: {
        '@bp0': {
          maxWidth: '$544',
          padding: '$24 $16 $16 $16',
          width: '100%',
        },
        '@bp2': {
          padding: '$24',
          width: '$544',
        },
        '@media (prefers-reduced-motion: no-preference)': {
          animation: `${fadeUp} 250ms forwards`,
        },
        borderRadius: '$20',
        height: 'auto',
        margin: 'auto 0',
      },
    },
    // eslint-disable-next-line canonical/sort-keys
    fullImage: {
      true: {
        padding: '0 !important',
      },
    },
  },
});

export const Title = styled(ModalPrimitive.Title, {
  '@bp0': {
    fontSize: '$23',
    fontWeight: 600,
    letterSpacing: '-$0_25',
    lineHeight: 1.1,
  },
  '@bp2': {
    fontSize: '$34',
    fontWeight: 700,
    letterSpacing: '-$0.5',
    lineHeight: '$44',
  },
  color: '$brandBlack',
  fontFamily: '$inter',
  margin: 0,
  variants: {
    variant: {
      modalSmol: {
        alignItems: 'center',
        display: 'inline-flex',
        fontSize: '$19',
        gap: '$8',
      },
    },
  },
});

export const Description = styled(ModalPrimitive.Description, {
  color: '$uiBlackMediumEmphasis',
  fontFamily: '$inter',
  fontSize: '$16',
  fontWeight: 400,
  lineHeight: 1.5,
  marginTop: '$16',
  variants: {
    variant: {
      modalSmol: {
        marginTop: '$12',
      },
    },
  },
});

export const CallToAction = styled('button', {
  '&:hover': {
    background: '$yellow10',
    cursor: 'pointer',
  },
  alignItems: 'center',
  background: '$brandYellow',
  borderRadius: '$25',
  color: '$brandBlack',
  display: 'flex',
  flexFlow: 'row nowrap',
  fontFamily: '$inter',
  fontSize: '$16',
  fontWeight: 600,
  height: '$48',
  justifyContent: 'center',
  lineHeight: '$24',
  marginTop: '$8',
  padding: '$12 $24',
  textAlign: 'center',
  transition: 'background-color $basicDuration ease',
});

const CloseButtonStyles = {
  '& > svg': {
    color: '$gray60',
  },
  cursor: 'pointer',
  position: 'absolute',
  right: '$24',
  top: '$24',
  variants: {
    fullImage: {
      true: {
        '& > svg': {
          color: '$white',
        },
      },
    },
  },
  zIndex: '$modalDialog',
};

export const CloseButtonContainer = styled('button', CloseButtonStyles);

export const Close = styled(ModalPrimitive.Close, CloseButtonStyles);

export const Body = styled('div', {
  alignItems: 'center',
  color: '$brandBlack',
  display: 'flex',
  flexFlow: 'column nowrap',
  marginTop: '-$8',
  textAlign: 'center',
  variants: {
    fullImage: {
      true: {
        '@bp0': {
          marginTop: '-$4',
          padding: '$24',
        },
        '@bp2': {
          marginTop: '-$4',
          padding: '$24 $44 $24',
        },
      },
    },
  },
});

// @deprecated Use button box instead
export const Buttons = styled('div', {
  '& > * + *': {
    marginTop: '$16',
  },
  '@bp0': {
    '& > *': {
      width: '100%',
    },
    margin: '$24 0 0',
  },
  '@bp1': {
    '& > *': {
      width: 'auto',
    },
    margin: '$32 0 0',
  },
  alignItems: 'center',
  display: 'flex',
  flexFlow: 'column nowrap',
  textAlign: 'center',
  width: '100%',
});

export const ButtonBox = styled('div', {
  '@bp0': {
    '& > *': {
      width: '100%',
    },
    flexFlow: 'column-reverse',
    gap: '$16',
    width: '100%',
  },
  '@bp2': {
    '& > *': {
      width: '50%',
    },
    flexFlow: 'row nowrap',
    gap: '$8',
    width: '100%',
  },
  alignItems: 'center',
  display: 'flex',
  marginTop: '$24',
  variants: {
    variant: {
      modalSmol: {
        '@bp0': {
          flexDirection: 'column-reverse',
          gap: '$16',
          width: '100%',
        },
        '@bp2': {
          flexDirection: 'row',
          gap: '$8',
          justifyContent: 'flex-end',
        },
      },
    },
  },
});

export const Image = styled('div', {
  marginBottom: '$24',
  variants: {
    fullImage: {
      true: {
        borderTopLeftRadius: '$20',
        borderTopRightRadius: '$20',
        marginBottom: 0,
        overflow: 'hidden',
        width: '100%',
      },
    },
  },
  width: '100%',
});

export const Divider = styled('div', {
  borderTop: 'solid $gray30',
  borderWidth: '$1',
  margin: '$24 0',
  width: '100%',
});
